import { FileReader } from '@/platform'

type MallNewBinSummary = {
  version: number
  goodsCount: number
  goodsPriceCount: number
}

function fail(message: string): never {
  throw new Error(`MallNew.bin 校验失败: ${message}`)
}

function ensureReadable(reader: FileReader, length: number, label: string) {
  if (length < 0) fail(`${label} 长度不能为负数: ${length}`)
  if (reader.getRemainingBytes() < length) {
    fail(`${label} 需要 ${length} 字节，实际仅剩 ${reader.getRemainingBytes()} 字节`)
  }
}

function readIntStrict(reader: FileReader, label: string) {
  ensureReadable(reader, 4, label)
  return reader.readInt()
}

function skipStringStrict(reader: FileReader, label: string) {
  const length = readIntStrict(reader, `${label}.length`)
  ensureReadable(reader, length, label)
  reader.skip(length)
}

function readChildReader(reader: FileReader, label: string) {
  const length = readIntStrict(reader, `${label}.blockLength`)
  ensureReadable(reader, length, label)
  return new FileReader(reader.readSome(length))
}

function ensureChildConsumed(reader: FileReader, label: string) {
  if (!reader.isReadEnd()) {
    fail(`${label} 存在 ${reader.getRemainingBytes()} 字节未消费`)
  }
}

function skipPoint(reader: FileReader, label: string) {
  readIntStrict(reader, `${label}.x`)
  readIntStrict(reader, `${label}.y`)
}

function skipViewSize(reader: FileReader, label: string) {
  readIntStrict(reader, `${label}.w`)
  readIntStrict(reader, `${label}.h`)
}

function skipFileName(reader: FileReader, label: string) {
  const child = readChildReader(reader, label)
  readIntStrict(child, `${label}.type`)
  skipStringStrict(child, `${label}.name`)
  ensureChildConsumed(child, label)
}

function skipFontProperty(reader: FileReader, label: string) {
  const child = readChildReader(reader, label)
  readIntStrict(child, `${label}.fontSize`)
  skipStringStrict(child, `${label}.fontName`)
  skipStringStrict(child, `${label}.fontColor`)
  ensureChildConsumed(child, label)
}

function skipViewport(reader: FileReader, label: string) {
  const child = readChildReader(reader, label)
  readIntStrict(child, `${label}.x`)
  readIntStrict(child, `${label}.y`)
  readIntStrict(child, `${label}.width`)
  readIntStrict(child, `${label}.height`)
  ensureChildConsumed(child, label)
}

function skipButtonIndex(reader: FileReader, label: string) {
  const child = readChildReader(reader, label)
  readIntStrict(child, `${label}.index`)
  readIntStrict(child, `${label}.x`)
  readIntStrict(child, `${label}.y`)
  ensureChildConsumed(child, label)
}

function skipOafIndexInfo(reader: FileReader, label: string) {
  const child = readChildReader(reader, label)
  readIntStrict(child, `${label}.actIndex`)
  skipStringStrict(child, `${label}.actName`)
  readIntStrict(child, `${label}.actType`)
  ensureChildConsumed(child, label)
}

function skipOafInfo(reader: FileReader, label: string) {
  const child = readChildReader(reader, label)
  skipStringStrict(child, `${label}.oafName`)
  const count = readIntStrict(child, `${label}.actionCount`)
  for (let index = 0; index < count; index++) {
    skipOafIndexInfo(child, `${label}.actions[${index}]`)
  }
  skipFileName(child, `${label}.faultTolerantGraph`)
  ensureChildConsumed(child, label)
}

function skipGoodDetailPage(reader: FileReader, label: string) {
  const child = readChildReader(reader, label)
  skipFileName(child, `${label}.bg`)
  skipFileName(child, `${label}.itemPicBg`)
  skipPoint(child, `${label}.itemPicBgPos`)
  skipViewSize(child, `${label}.itemPicBgSize`)
  skipPoint(child, `${label}.itemPicPos`)
  skipViewSize(child, `${label}.itemPicSize`)
  skipPoint(child, `${label}.itemNamePos`)
  skipPoint(child, `${label}.itemAllPriceTxtPos`)
  skipPoint(child, `${label}.itemAllPricePicPos`)
  skipPoint(child, `${label}.itemAllPriceNumPos`)
  skipViewport(child, `${label}.itemDescSize`)
  skipButtonIndex(child, `${label}.confirmBtn`)
  skipButtonIndex(child, `${label}.cancelBtn`)
  skipButtonIndex(child, `${label}.minusBtn`)
  skipButtonIndex(child, `${label}.addBtn`)
  readIntStrict(child, `${label}.isUserPic`)
  readIntStrict(child, `${label}.varIndex`)
  skipFileName(child, `${label}.varIndexPic`)
  skipPoint(child, `${label}.buyNumPos`)
  readIntStrict(child, `${label}.isShowBuyNumber`)
  readIntStrict(child, `${label}.isShowItemName`)
  readIntStrict(child, `${label}.isShowItemDesc`)
  readIntStrict(child, `${label}.isShowItemPic`)
  ensureChildConsumed(child, label)
}

function skipMallItemAttribute(reader: FileReader, label: string) {
  const child = readChildReader(reader, label)
  readIntStrict(child, `${label}.attributeId`)
  readIntStrict(child, `${label}.operator`)
  readIntStrict(child, `${label}.value`)
  ensureChildConsumed(child, label)
}

function skipGoodData(reader: FileReader, label: string) {
  const child = readChildReader(reader, label)
  readIntStrict(child, `${label}.itemId`)
  skipPoint(child, `${label}.itemPoint`)
  skipStringStrict(child, `${label}.itemName`)
  skipFontProperty(child, `${label}.itemNameFont`)
  skipPoint(child, `${label}.itemNamePos`)
  readIntStrict(child, `${label}.itemType`)
  readIntStrict(child, `${label}.itemPrice`)
  skipFontProperty(child, `${label}.itemPriceFont`)
  skipPoint(child, `${label}.itemPricePos`)
  skipStringStrict(child, `${label}.itemDesc`)
  skipFontProperty(child, `${label}.itemDescFont`)
  skipViewport(child, `${label}.itemDescSize`)
  skipFileName(child, `${label}.itemViewBg`)
  skipViewSize(child, `${label}.itemViewBgSize`)
  skipFileName(child, `${label}.itemPic`)
  skipPoint(child, `${label}.itemPicPos`)
  skipViewSize(child, `${label}.itemPicSize`)
  skipFileName(child, `${label}.itemPicBg`)
  skipPoint(child, `${label}.itemPicBgPos`)
  skipViewSize(child, `${label}.itemPicBgSize`)
  skipPoint(child, `${label}.itemPriceFlowerPos`)
  skipButtonIndex(child, `${label}.itemCommityBtn`)
  skipFileName(child, `${label}.itemEnablePic`)
  skipFileName(child, `${label}.itemDisabledPic`)
  readIntStrict(child, `${label}.isRepeat`)
  readIntStrict(child, `${label}.itemStatus`)
  readIntStrict(child, `${label}.isHpLock`)
  readIntStrict(child, `${label}.itemFree`)
  skipStringStrict(child, `${label}.itemAttribute`)
  const attributeCount = readIntStrict(child, `${label}.attributeCount`)
  for (let index = 0; index < attributeCount; index++) {
    skipMallItemAttribute(child, `${label}.attributes[${index}]`)
  }
  ensureChildConsumed(child, label)
}

export function verifyMallNewBinBytes(bytes: Uint8Array | ArrayBuffer): MallNewBinSummary {
  const reader = new FileReader(bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes))

  const version = readIntStrict(reader, 'mall.version')
  skipFileName(reader, 'mall.backPic')
  skipFileName(reader, 'mall.halfPic')
  skipPoint(reader, 'mall.halfPos')
  readIntStrict(reader, 'mall.isOaf')
  skipOafInfo(reader, 'mall.oafInfo')
  skipButtonIndex(reader, 'mall.closeBtn')
  skipFontProperty(reader, 'mall.flowerText')
  skipFileName(reader, 'mall.flowerPic')
  skipPoint(reader, 'mall.flowerPicPos')
  skipPoint(reader, 'mall.flowerTextPos')
  skipPoint(reader, 'mall.flowerNumberPos')
  skipGoodDetailPage(reader, 'mall.goodDetailPage')
  skipFileName(reader, 'mall.goodAreaBack')
  skipPoint(reader, 'mall.goodAreaBackPos')
  skipViewSize(reader, 'mall.goodAreaBackSize')
  readIntStrict(reader, 'mall.showGoodName')
  readIntStrict(reader, 'mall.showGoodDesc')
  readIntStrict(reader, 'mall.showGoodPic')

  const goodsCount = readIntStrict(reader, 'mall.goodsCount')
  for (let index = 0; index < goodsCount; index++) {
    skipGoodData(reader, `mall.goods[${index}]`)
  }

  skipButtonIndex(reader, 'mall.previousPageBtn')
  skipButtonIndex(reader, 'mall.nextPageBtn')
  skipFontProperty(reader, 'mall.pageNumber')
  skipPoint(reader, 'mall.pageNumberPos')
  skipButtonIndex(reader, 'mall.tipBtn')
  skipStringStrict(reader, 'mall.tipName')

  const goodsPriceCount = readIntStrict(reader, 'mall.goodsPriceCount')
  for (let index = 0; index < goodsPriceCount; index++) {
    readIntStrict(reader, `mall.goodsPrices[${index}]`)
  }

  skipStringStrict(reader, 'mall.publishTime')
  readIntStrict(reader, 'mall.modifyPosition')
  readIntStrict(reader, 'mall.showGetAll')
  skipButtonIndex(reader, 'mall.getAllBtn')

  if (goodsPriceCount !== goodsCount) {
    fail(`商品数量(${goodsCount})与丸子价格数量(${goodsPriceCount})不一致`)
  }

  if (!reader.isReadEnd()) {
    fail(`顶层结构后仍残留 ${reader.getRemainingBytes()} 字节`)
  }

  return {
    version,
    goodsCount,
    goodsPriceCount,
  }
}
